import { useEffect, useRef } from 'react';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { smoothFocusItem } from '../utils/smoothScroll';

export function useFocusFirstError() {
  const errors = QuestionnaireTemplateStore.useStoreState((s) => s.errors);
  const hasFocusedRef = useRef(false);
  const prevErrorCountRef = useRef(0);

  useEffect(() => {
    const errorKeys = Object.keys(errors);
    const errorCount = errorKeys.length;

    // Only trigger focus when errors are freshly populated (e.g. on save)
    // not when errors are being cleared as user fixes them
    const isNewErrorBatch = errorCount > 0 && errorCount > prevErrorCountRef.current;
    prevErrorCountRef.current = errorCount;

    if (!isNewErrorBatch && hasFocusedRef.current) return;
    if (!errorCount) {
      hasFocusedRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const candidates: { el: HTMLElement; top: number }[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const key of errorKeys) {
        const el = document.querySelector<HTMLElement>(`[data-error-key="${key}"]`);
        if (el) {
          candidates.push({ el, top: el.getBoundingClientRect().top });
        }
      }

      if (!candidates.length) return;

      const activeEl = document.activeElement;
      const isAlreadyOnErrorField = candidates.some((c) => c.el === activeEl || c.el.contains(activeEl));
      if (isAlreadyOnErrorField) return;

      candidates.sort((a, b) => a.top - b.top);
      smoothFocusItem(candidates[0].el);
      hasFocusedRef.current = true;
    }, 60);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [errors]);
}
